import isEmpty from 'lodash/isEmpty';
import { NextPage } from 'next';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import ErrorPage from '@/components/route/error/ErrorPage/ErrorPage';
import HeadMeta from '@/components/static-pages/HeadMeta';
import TravelCreditsTemplate from '@/components/templates/TravelCreditsTemplate';
import { useRouter } from '@/hooks/useRouter';
import { TRootState } from '@/redux/rootReducer';
import { GENERIC_ERROR_MESSAGE } from '@/services/apiRequest';
import { getStaticPageData } from '@/services/getPageData';
import { getLocale } from '@/utility/i18n';
import { getAuthToken } from '@/utility/session';

interface TravelCreditsProps {
  title?: string;
  description?: string;
  seoUrl?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contentBlocks?: { type: string; value: any }[];
  error?: ErrorProps;
  noindex?: boolean;
}
interface ErrorProps {
  statusCode: number;
  message: string;
}

const TravelCredits: NextPage<TravelCreditsProps> = ({
  title = '',
  description = '',
  seoUrl = '',
  contentBlocks = [],
  error,
  noindex,
}) => {
  const router = useRouter();
  const isAuthenticated = useSelector<TRootState>(state => state.auth.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated && !getAuthToken()) {
      router.push('/users/new');
    }
  }, [isAuthenticated, router]);

  if (error) {
    return <ErrorPage statusCode={error.statusCode} message={error.message} />;
  }

  return (
    <>
      <HeadMeta title={title} description={description} url={seoUrl} noindex={noindex} />
      <TravelCreditsTemplate contentBlocks={contentBlocks} />
    </>
  );
};

TravelCredits.getInitialProps = async ({ res, locale: l }) => {
  try {
    const locale = getLocale(l);
    const pageData = await getStaticPageData({ locale, url_path: 'travel-credits' });

    if (isEmpty(pageData) || isEmpty(pageData.metadata)) {
      throw new Error(GENERIC_ERROR_MESSAGE);
    }

    const { seo_title, seo_description, url, content_blocks, title } = pageData.metadata;

    return {
      title: seo_title ?? title,
      description: seo_description,
      seoUrl: url,
      contentBlocks: content_blocks,
      noindex: !!pageData.fallback,
    };
  } catch {
    if (res) {
      res.statusCode = 404;
    }
    return {
      error: { statusCode: 404, message: GENERIC_ERROR_MESSAGE },
    };
  }
};

export default TravelCredits;
